import React, { useState, useEffect } from 'react';
import { Key, ArrowLeft, Shield, AlertTriangle, Lock, Server, Bot, Settings, X, Check, Loader } from 'lucide-react';

import type { CredentialData } from './CredentialModal';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';
import SingleSelectApiDropdown from '../../../components/SingleSelectApiDropdown';
// import { showError, showSuccess } from '../../utlis/toast';
// import baseapi from '../../services/baseApi';



interface CredentialFormProps {
    agent: {
        client_id: string;
        id: string;
    };
    organizationId: string;
    credential: CredentialData | null;
    isEdit: boolean;
    onSuccess: () => void;
    onBack: () => void;
}

// const isValidIPv4 = (ip: string): boolean => {
//     const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
//     return ipv4Regex.test(ip);
// };

const CredentialForm: React.FC<CredentialFormProps> = ({
    agent,
    organizationId,
    credential,
    isEdit,
    onSuccess,
    onBack
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [formData, setFormData] = useState<CredentialData>({
        credential_name: '',
        credential_type: 'snmp',
        enabled: true,
        is_active: true,
        snmp_type: 'v1',
        community_string: '',
        snmp_username: '',
        auth_password: '',
        privacy_password: '',
        firewall_type: 'cisco',
        vmware_type: 'normal',
        cidrs: [],
        host: '',
        port: 443,
        log_monitor_enable: false,
        syslog_port: 443,
        channel: ''
    });

    const [ipAddresses, setIpAddresses] = useState<string[]>([]);

    // const [ipErrors, setIpErrors] = useState<string | null>(null);
    const [selectedAsset, setSelectedAsset] = useState<any>(null);

    // dropdowns for credential type
    const credentialTypeOptions = [
        { label: 'SNMP', value: 'snmp' },
        { label: 'Firewall', value: 'firewall' },
        { label: 'VMware', value: 'vmware' },
        { label: 'Camera', value: 'camera' },
        { label: 'Discovery', value: 'discovery' },
        { label: 'iLO', value: 'ilo' },
        { label: 'NVR', value: 'nvr' },
        { label: 'Access Point', value: 'access_point' }
    ];
    const snmpTypeOptions = [
        { label: 'SNMP v1', value: 'v1' },
        { label: 'SNMP v2', value: 'v2c' },
        { label: 'SNMP v3', value: 'v3' }
    ];
    const firewallTypeOptions = [
        { label: 'Cisco', value: 'cisco' },
        { label: 'FortiGate', value: 'fortigate' }
    ];
    const vmwareTypeOptions = [
        { label: 'Normal', value: 'normal' },
        { label: 'vCenter', value: 'vcenter' }
    ];

    useEffect(() => {
        if (isEdit && credential) {
            let parsedData: any = {};
            if (credential.credential_data) {
                try {
                    parsedData = typeof credential.credential_data === 'string'
                        ? JSON.parse(credential.credential_data)
                        : credential.credential_data;
                } catch (e) {
                    console.error('Failed to parse credential_data:', e);
                }
            }

            setFormData({
                ...credential,
                // Map fields from parsedData to formData
                snmp_type: parsedData.snmp_version || (credential as any).snmp_type,
                community_string: parsedData.community || (credential as any).community_string,
                snmp_username: parsedData.snmp_username || (credential as any).snmp_username,
                auth_protocol: parsedData.auth_protocol || (credential as any).auth_protocol,
                auth_password: parsedData.auth_password || (credential as any).auth_password,
                privacy_protocol: parsedData.priv_protocol || (credential as any).privacy_protocol,
                privacy_password: parsedData.priv_password || (credential as any).privacy_password,

                firewall_type: parsedData.firewall_type || (credential as any).firewall_type,
                hostname: parsedData.hostname || (credential as any).hostname,
                ssh_port: parsedData.ssh_port || (credential as any).ssh_port,
                https_port: parsedData.https_port || (credential as any).https_port,
                username: parsedData.username || (credential as any).username,
                password: parsedData.password || (credential as any).password,
                api_key: parsedData.api_key || (credential as any).api_key,
                log_monitor_enable: parsedData.log_monitor_enable || (credential as any).log_monitor_enable || false,
                syslog_port: parsedData.syslog_port || (credential as any).syslog_port || 514,

                vmware_type: parsedData.vmware_type || (credential as any).vmware_type,
                port: parsedData.port || (credential as any).port || parsedData.https_port || 443,
                private_key: parsedData.private_key || (credential as any).private_key,
                vcenter_host: parsedData.vcenter_host || (credential as any).vcenter_host,
                make: parsedData.make || (credential as any).make || '',
                channel: parsedData.channel || (credential as any).channel || '',
                cidrs: parsedData.cidrs || (credential as any).cidrs || [],
                host: parsedData.host || (credential as any).host || '',
                nvr_auth_type: parsedData.nvr_auth_type || (parsedData.username ? 'admin' : 'snmp'),
                is_active: credential.is_active ?? true
            });

            if (credential.asset_info.ip_address) {
                setIpAddresses([credential.asset_info.ip_address]);
            } else {
                setIpAddresses([]);
            }

            // Set selected asset if available
            if (credential.asset_info) {
                setSelectedAsset(credential.asset_info);
            } else if (credential.asset_id) {
                // Fetch asset details if ONLY ID is present
                const fetchAsset = async () => {
                    try {
                        const response = await baseapi.get(`/assets/${credential.asset_id}`);
                        if (response.data.status) {
                            setSelectedAsset(response.data.data);
                        }
                    } catch (e) {
                        console.error('Failed to fetch asset details:', e);
                    }
                };
                fetchAsset();
            }
        }
    }, [isEdit, credential]);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.credential_name.trim()) {
            newErrors.credential_name = 'Credential name is required';
        }

        // Must have at least one IP (from selected asset) - Not required for discovery
        if (formData.credential_type !== 'discovery' && ipAddresses.length < 1) {
            newErrors.asset_id = 'Please select an asset';
        }

        // SNMP/Access Point Validation
        if (formData.credential_type === 'snmp' || formData.credential_type === 'access_point') {
            if (formData.snmp_type !== 'v3') {
                if (!formData.community_string?.trim()) {
                    newErrors.community_string = 'Community string is required';
                }
            } else {
                if (!formData.snmp_username?.trim()) {
                    newErrors.snmp_username = 'SNMP Username is required';
                }
                if (!formData.auth_protocol?.trim()) {
                    newErrors.auth_protocol = 'Auth protocol is required for SNMP v3';
                }
                if (!formData.auth_password?.trim()) {
                    newErrors.auth_password = 'Auth password is required for SNMP v3';
                }
                if (!formData.privacy_protocol?.trim()) {
                    newErrors.privacy_protocol = 'Privacy protocol is required for SNMP v3';
                }
                if (!formData.privacy_password?.trim()) {
                    newErrors.privacy_password = 'Privacy password is required for SNMP v3';
                }
            }
        }

        // NVR Validation
        if (formData.credential_type === 'nvr') {
            if (formData.nvr_auth_type === 'admin') {
                if (!formData.username?.trim()) {
                    newErrors.username = 'Username is required';
                }
                if (!formData.password?.trim()) {
                    newErrors.password = 'Password is required';
                }
                if (!formData.port) {
                    newErrors.port = 'Port is required';
                }
            } else {
                if (!formData.community_string?.trim()) {
                    newErrors.community_string = 'Community string is required';
                }
            }
        }

        // Firewall
        if (formData.credential_type === 'firewall') {
            // Hostname is auto-set from selected asset in handleSubmit
            if (formData.firewall_type === 'cisco') {
                if (!formData.ssh_port) {
                    newErrors.ssh_port = 'SSH port is required';
                }
                if (!formData.username?.trim()) {
                    newErrors.username = 'Username is required';
                }
                if (!formData.password?.trim()) {
                    newErrors.password = 'Password is required';
                }
            } else if (formData.firewall_type === 'fortigate') {
                if (!formData.https_port) {
                    newErrors.https_port = 'HTTPS port is required';
                }
                if (!formData.api_key?.trim()) {
                    newErrors.api_key = 'API key is required';
                }
                if (formData.log_monitor_enable && !formData.syslog_port) {
                    newErrors.syslog_port = 'Syslog port is required';
                }
            }
        }

        // VMware
        if (formData.credential_type === 'vmware') {
            if (!formData.port) {
                newErrors.port = 'Port is required';
            }
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
            if (!formData.private_key?.trim()) {
                newErrors.private_key = 'Private key is required';
            }
            if (formData.vmware_type === 'vcenter' && !formData.vcenter_host?.trim()) {
                newErrors.vcenter_host = 'vCenter host is required';
            }
        }

        // Camera
        if (formData.credential_type === 'camera') {
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
        }

        // Discovery Validation
        if (formData.credential_type === 'discovery') {
            if (!formData.cidrs || formData.cidrs.length === 0) {
                newErrors.cidrs = 'At least one CIDR is required';
            } else {
                const invalidCidrs = formData.cidrs.filter(cidr => {
                    const parts = cidr.split('/');
                    if (parts.length !== 2) return true;
                    const [ip, prefixStr] = parts;
                    const prefix = parseInt(prefixStr, 10);
                    if (isNaN(prefix) || prefix < 16 || prefix > 32) return true;
                    const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                    return !ipv4Regex.test(ip);
                });
                if (invalidCidrs.length > 0) {
                    newErrors.cidrs = 'Invalid CIDR format or prefix not in /16 to /32 range';
                }
            }
        }

        // iLO Validation
        if (formData.credential_type === 'ilo') {
            if (!formData.host?.trim()) {
                newErrors.host = 'Host is required';
            }
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        // 1. Create a base object with common fields
        const basePayload = {
            client_id: Number(agent.client_id),
            agent_id: Number(agent.id),
            organization_id: organizationId,
            credential_name: formData.credential_name,
            credential_type: formData.credential_type,
            enabled: formData.enabled,
            is_active: formData.is_active,
            ip_addresses: ipAddresses,
            asset_id: selectedAsset?.id || null
        };

        // 2. Create a type-specific payload
        let typeSpecificData = {};

        switch (formData.credential_type) {
            case 'snmp':
            case 'access_point':
                typeSpecificData = {
                    snmp_version: formData.snmp_type,
                    // Conditional data based on version
                    ...(formData.snmp_type !== 'v3' ? {
                        community: formData.community_string,
                    } : {
                        snmp_username: formData.snmp_username,
                        auth_protocol: formData.auth_protocol,
                        auth_password: formData.auth_password,
                        priv_protocol: formData.privacy_protocol,
                        priv_password: formData.privacy_password
                    }),
                    make: formData.make
                };
                break;

            case 'nvr':
                if (formData.nvr_auth_type === 'admin') {
                    typeSpecificData = {
                        nvr_auth_type: 'admin',
                        username: formData.username,
                        password: formData.password,
                        port: formData.port || 80,
                        make: formData.make
                    };
                } else {
                    typeSpecificData = {
                        nvr_auth_type: 'snmp',
                        snmp_version: 'v2c',
                        community: formData.community_string,
                        make: formData.make
                    };
                }
                break;

            case 'firewall':
                typeSpecificData = {
                    firewall_type: formData.firewall_type,
                    hostname: selectedAsset ? (selectedAsset.name || selectedAsset.ip_address) : formData.hostname,
                };

                if (formData.firewall_type === 'cisco') {
                    typeSpecificData = {
                        ...typeSpecificData,
                        ssh_port: formData.ssh_port,
                        username: formData.username,
                        password: formData.password
                    };
                } else if (formData.firewall_type === 'fortigate') {
                    typeSpecificData = {
                        ...typeSpecificData,
                        https_port: formData.https_port,
                        api_key: formData.api_key,
                        log_monitor_enable: formData.log_monitor_enable || false,
                        ...(formData.log_monitor_enable && {
                            syslog_port: formData.syslog_port || 514
                        })
                    };
                }
                break;

            case 'vmware':
                typeSpecificData = {
                    vmware_type: formData.vmware_type,
                    port: formData.port,
                    username: formData.username,
                    password: formData.password,
                    private_key: formData.private_key,
                    // Only include vcenter_host if vcenter is selected
                    ...(formData.vmware_type === 'vcenter' && {
                        vcenter_host: formData.vcenter_host
                    })
                };
                break;

            case 'camera':
                typeSpecificData = {
                    username: formData.username,
                    password: formData.password,
                    make: formData.make,
                    channel: formData.channel !== '' ? Number(formData.channel) : null
                };
                break;

            case 'discovery':
                typeSpecificData = {
                    cidrs: formData.cidrs
                };
                break;

            case 'ilo':
                typeSpecificData = {
                    host: formData.host,
                    username: formData.username,
                    password: formData.password,
                    port: formData.port || 443
                };
                break;
        }

        // 3. Merge them into the final payload
        const finalPayload = {
            ...basePayload,
            credential_data: {
                ...typeSpecificData
            }
        };


        try {
            let credentialId: string | undefined;
            if (isEdit && credential?.id) {
                credentialId = credential.id;
            }

            const endpoint = isEdit ? `/credentials/${credentialId}` : '/credentials';
            const method = isEdit ? 'put' : 'post';

            // Use finalPayload instead of params
            const response = await baseapi[method](endpoint, finalPayload);

            if (response.data.status) {
                showSuccess(isEdit ? 'Credential updated successfully' : 'Credential configured successfully');
                onSuccess();
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Submit failed:', error);
            showError('Failed to save credential configuration');
        } finally {
            setIsSubmitting(false);
        }
    };


    // IP Entry Handlers
    // const handleAddIpAddress = () => {
    //     if (!newIpAddress.trim()) return;
    //     if (!isValidIPv4(newIpAddress.trim())) {
    //         setIpErrors('Invalid IP address format');
    //         return;
    //     }
    //     if (ipAddresses.includes(newIpAddress.trim())) {
    //         setIpErrors('Duplicate IP address');
    //         return;
    //     }
    //     setIpAddresses([...ipAddresses, newIpAddress.trim()]);
    //     setNewIpAddress('');
    //     setIpErrors(null);
    // };
    // const handleRemoveIpAddress = (index: number) => {
    //     setIpAddresses(ipAddresses.filter((_, idx) => idx !== index));
    // };

    // Credential fields rendering
    const renderCredentialFields = () => {
        const inputBaseClass = `w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#422568]/50 transition-all duration-200 font-mono`;
        const labelBaseClass = `flex items-center text-[10px] font-bold text-gray-800 uppercase tracking-[0.1em] mb-1.5`;
        const errorClass = `mt-1 text-[9px] text-red-400 font-bold uppercase tracking-tight flex items-center`;

        switch (formData.credential_type) {
            case 'snmp':
            case 'access_point':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Shield className="w-3 h-3 mr-1.5 text-blue-400" />
                                SNMP Version <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={snmpTypeOptions}
                                placeholder="Select Version"
                                selectedValue={formData.snmp_type || 'v1'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        snmp_type: value as 'v1' | 'v2c' | 'v3',
                                        community_string: '',
                                        snmp_username: '',
                                        auth_protocol: '',
                                        auth_password: '',
                                        privacy_protocol: '',
                                        privacy_password: ''
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.snmp_type !== 'v3' ? (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>
                                    <Key className="w-3 h-3 mr-1.5 text-cyan-400" />
                                    Community String <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.community_string || ''}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, community_string: e.target.value }));
                                        if (errors.community_string) {
                                            setErrors(prev => {
                                                const { community_string, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    className={inputBaseClass}
                                    placeholder="Enter community string"
                                />
                                {errors.community_string && (
                                    <p className={errorClass}>
                                        <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                        {errors.community_string}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-cyan-400" />
                                        SNMP Username <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.snmp_username || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, snmp_username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Username"
                                    />
                                    {errors.snmp_username && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.snmp_username}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-green-400" />
                                        Auth Protocol
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.auth_protocol || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, auth_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="MD5/SHA"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-green-400" />
                                        Auth Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.auth_password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, auth_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-purple-400" />
                                        Privacy Protocol
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.privacy_protocol || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, privacy_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="DES/AES"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-purple-400" />
                                        Privacy Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.privacy_password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, privacy_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-[#422568]" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Cisco, HP, Aruba"
                            />
                        </div>
                    </div>
                );
            case 'nvr':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2 text-xs">
                            <div className="bg-[#422568]/5 border border-[#422568]/20 rounded-lg p-3 mb-1">
                                <p className="text-[10px] text-[#422568] font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                                    <Shield className="w-3.5 h-3.5" />
                                    NVR Authentication Configuration
                                </p>
                                <p className="text-[9px] text-gray-800 leading-relaxed font-medium">
                                    Choose whether to query this NVR device via SNMP v2 protocol or using Admin authentication credentials (username, password, and port).
                                </p>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                NVR Access Protocol <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={[{ label: 'SNMP Protocol', value: 'snmp' }, { label: 'Admin Credentials', value: 'admin' }]}
                                placeholder="Select Protocol"
                                selectedValue={formData.nvr_auth_type || 'snmp'}
                                onSelect={(val) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        nvr_auth_type: val as 'snmp' | 'admin'
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>

                        {formData.nvr_auth_type === 'admin' ? (
                            <>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-cyan-400" />
                                        Username <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username || ''}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, username: e.target.value }));
                                            if (errors.username) {
                                                setErrors(prev => {
                                                    const { username, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        className={inputBaseClass}
                                        placeholder="Enter admin username"
                                    />
                                    {errors.username && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.username}
                                        </p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-rose-400" />
                                        Password <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password || ''}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, password: e.target.value }));
                                            if (errors.password) {
                                                setErrors(prev => {
                                                    const { password, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        className={inputBaseClass}
                                        placeholder="Enter admin password"
                                    />
                                    {errors.password && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.password}
                                        </p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Shield className="w-3 h-3 mr-1.5 text-[#422568]" />
                                        Port <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.port || ''}
                                        onChange={(e) => {
                                            const p = Number(e.target.value) || 0;
                                            setFormData(prev => ({ ...prev, port: p }));
                                            if (errors.port) {
                                                setErrors(prev => {
                                                    const { port, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        className={inputBaseClass}
                                        placeholder="e.g. 80, 554, 37777"
                                    />
                                    {errors.port && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.port}
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Shield className="w-3 h-3 mr-1.5 text-blue-400" />
                                        SNMP Version <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <CustomDropdown
                                        data={snmpTypeOptions}
                                        placeholder="Select Version"
                                        selectedValue={'v2c'}
                                        onSelect={() => { }}
                                        width="w-full"
                                        buttonHeight={8}
                                        disabled={true}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-cyan-400" />
                                        Community String <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.community_string || ''}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, community_string: e.target.value }));
                                            if (errors.community_string) {
                                                setErrors(prev => {
                                                    const { community_string, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        className={inputBaseClass}
                                        placeholder="Enter community string"
                                    />
                                    {errors.community_string && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.community_string}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-[#422568]" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Hikvision, Dahua"
                            />
                        </div>
                    </div>
                );
            case 'firewall':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Shield className="w-3 h-3 mr-1.5 text-red-400" />
                                Firewall Vendor <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={firewallTypeOptions}
                                placeholder="Select Vendor"
                                selectedValue={formData.firewall_type || 'cisco'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        firewall_type: value as 'cisco' | 'fortigate',
                                        ssh_port: undefined,
                                        https_port: undefined,
                                        username: '',
                                        password: '',
                                        api_key: '',
                                        log_monitor_enable: false,
                                        syslog_port: 514
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.firewall_type === 'cisco' ? (
                            <>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                        SSH Port
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.ssh_port || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, ssh_port: parseInt(e.target.value) || undefined }))}
                                        className={inputBaseClass}
                                        placeholder="22"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                        User
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="User"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                        HTTPS Port
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.https_port || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, https_port: parseInt(e.target.value) || undefined }))}
                                        className={inputBaseClass}
                                        placeholder="443"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className={labelBaseClass}>
                                        <Shield className="w-3 h-3 mr-1.5 text-[#422568]" />
                                        Log Monitor
                                    </label>
                                    <div className="flex items-center h-[26px]">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                log_monitor_enable: !prev.log_monitor_enable,
                                                syslog_port: !prev.log_monitor_enable ? (prev.syslog_port || 514) : prev.syslog_port
                                            }))}
                                            className="flex items-center gap-2 group transition-all focus:outline-none"
                                        >
                                            <div className={`
                                                relative w-8 h-4 rounded-full transition-all duration-300 ease-in-out border
                                                ${formData.log_monitor_enable
                                                    ? 'bg-emerald-600 border-emerald-500/30'
                                                    : 'bg-white border-slate-200'}
                                            `}>
                                                <div className={`
                                                    absolute top-[2px] w-2.5 h-2.5 rounded-full bg-white transition-all duration-300 ease-in-out
                                                    ${formData.log_monitor_enable ? 'left-[16px]' : 'left-[2px]'}
                                                `} />
                                            </div>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${formData.log_monitor_enable ? 'text-emerald-400' : 'text-gray-800'}`}>
                                                {formData.log_monitor_enable ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                {formData.log_monitor_enable && (
                                    <div className="md:col-span-2">
                                        <label className={labelBaseClass}>
                                            <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                            System Log Port <span className="text-red-500 ml-0.5">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.syslog_port ?? ''}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, syslog_port: parseInt(e.target.value) || undefined }));
                                                if (errors.syslog_port) {
                                                    setErrors(prev => {
                                                        const { syslog_port, ...rest } = prev;
                                                        return rest;
                                                    });
                                                }
                                            }}
                                            className={inputBaseClass}
                                            placeholder="514"
                                        />
                                        {errors.syslog_port && (
                                            <p className={errorClass}>
                                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                                {errors.syslog_port}
                                            </p>
                                        )}
                                    </div>
                                )}
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-purple-400" />
                                        API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.api_key || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="FortiGate Key"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                );
            case 'vmware':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-green-400" />
                                Type <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={vmwareTypeOptions}
                                placeholder="Select Type"
                                selectedValue={formData.vmware_type || 'normal'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        vmware_type: value as 'normal' | 'vcenter',
                                        vcenter_host: ''
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.vmware_type === 'vcenter' && (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>
                                    <Server className="w-3 h-3 mr-1.5 text-cyan-400" />
                                    vCenter Host
                                </label>
                                <input
                                    type="text"
                                    value={formData.vcenter_host || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, vcenter_host: e.target.value }))}
                                    className={inputBaseClass}
                                    placeholder="vCenter Host"
                                />
                            </div>
                        )}
                        <div>
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                Port
                            </label>
                            <input
                                type="number"
                                value={formData.port || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) || undefined }))}
                                className={inputBaseClass}
                                placeholder="443"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Username"
                            />
                        </div>
                        <div className="md:col-span-2 text-xs">
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-purple-400" />
                                Private Key
                            </label>
                            <textarea
                                value={formData.private_key || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, private_key: e.target.value }))}
                                rows={3}
                                className={`${inputBaseClass} resize-none h-20 text-[9px]`}
                                placeholder="Paste key content..."
                            />
                        </div>
                    </div>
                );
            case 'camera':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="User"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                Password
                            </label>
                            <input
                                type="password"
                                value={formData.password || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-blue-400" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Hikvision, Dahua"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-[#422568]" />
                                Channel (Optional)
                            </label>
                            <input
                                type="number"
                                value={formData.channel || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, channel: e.target.value === '' ? '' : Number(e.target.value) }))}
                                className={inputBaseClass}
                                placeholder="e.g. 1"
                            />
                        </div>
                    </div >
                );
            case 'discovery':
                return (
                    <div className="space-y-4">
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <label className={labelBaseClass}>
                                    <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                    Add CIDR (e.g., 192.168.1.0/24) <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="new-cidr-input"
                                    className={inputBaseClass}
                                    placeholder="192.168.1.0/24"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = (e.target as HTMLInputElement).value.trim();
                                            if (val) {
                                                // Validate CIDR before adding
                                                const parts = val.split('/');
                                                const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                                const isValid = parts.length === 2 &&
                                                    ipv4Regex.test(parts[0]) &&
                                                    !isNaN(parseInt(parts[1])) &&
                                                    parseInt(parts[1]) >= 16 &&
                                                    parseInt(parts[1]) <= 32;

                                                if (!isValid) {
                                                    setErrors(prev => ({ ...prev, cidrs: 'Invalid CIDR format or prefix not in /16 to /32 range' }));
                                                    return;
                                                }

                                                setFormData(prev => ({
                                                    ...prev,
                                                    cidrs: [...(prev.cidrs || []), val]
                                                }));
                                                (e.target as HTMLInputElement).value = '';
                                                setErrors(prev => {
                                                    const { cidrs, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const input = document.getElementById('new-cidr-input') as HTMLInputElement;
                                    const val = input.value.trim();
                                    if (val) {
                                        // Validate CIDR before adding
                                        const parts = val.split('/');
                                        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                        const isValid = parts.length === 2 &&
                                            ipv4Regex.test(parts[0]) &&
                                            !isNaN(parseInt(parts[1])) &&
                                            parseInt(parts[1]) >= 16 &&
                                            parseInt(parts[1]) <= 32;

                                        if (!isValid) {
                                            setErrors(prev => ({ ...prev, cidrs: 'Invalid CIDR format or prefix not in /16 to /32 range' }));
                                            return;
                                        }

                                        setFormData(prev => ({
                                            ...prev,
                                            cidrs: [...(prev.cidrs || []), val]
                                        }));
                                        input.value = '';
                                        setErrors(prev => {
                                            const { cidrs, ...rest } = prev;
                                            return rest;
                                        });
                                    }
                                }}
                                className="bg-[#422568]/20 hover:bg-[#422568] text-[#422568] hover:text-white border border-[#422568]/30 px-4 py-1.5 rounded text-[10px] uppercase font-bold transition-all transition-colors h-[28px]"
                            >
                                Add
                            </button>
                        </div>
                        {errors.cidrs && (
                            <p className={errorClass}>
                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                {errors.cidrs}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {formData.cidrs?.map((cidr, index) => (
                                <div key={index} className="bg-white border border-slate-200 px-2 py-1 rounded flex items-center gap-2 group transition-all hover:border-[#422568]/30">
                                    <span className="text-[11px] font-mono text-gray-800">{cidr}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                cidrs: prev.cidrs?.filter((_, i) => i !== index)
                                            }));
                                        }}
                                        className="text-gray-800 hover:text-red-400 transition-colors"
                                    >
                                        <X className="w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 'ilo':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                Host <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.host || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, host: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Enter Host IP or Hostname"
                            />
                            {errors.host && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.host}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Username"
                            />
                            {errors.username && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.username}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                Password <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="password"
                                value={formData.password || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                            {errors.password && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.password}
                                </p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-[#422568]" />
                                Port (Optional)
                            </label>
                            <input
                                type="number"
                                value={formData.port || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) || undefined }))}
                                className={inputBaseClass}
                                placeholder="443"
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="p-4 bg-white">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-800">
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={onBack}
                        className="mr-2 text-gray-800 hover:text-white transition-colors p-1 rounded hover:bg-white"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="bg-[#422568]/20 p-1.5 rounded">
                            <Settings
                                className="h-4 w-4 text-[#422568]"
                                style={isSubmitting ? { animation: 'spin 3s linear infinite' } : {}}
                            />
                        </div>
                        <h2 className="text-base font-bold text-gray-800">
                            {isEdit ? 'Edit Configuration' : 'New Configuration'}
                        </h2>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {/* Basic Info Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center text-[10px] font-bold text-gray-800 uppercase tracking-widest mb-1.5">
                            <Key className="w-3 h-3 mr-1.5 text-[#422568]" />
                            Credential Name <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.credential_name}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, credential_name: e.target.value }));
                                if (errors.credential_name) {
                                    setErrors(prev => {
                                        const { credential_name, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                            className={`w-full bg-white border ${errors.credential_name ? 'border-red-500/50' : 'border-slate-200'} rounded px-3 py-1.5 text-[11px] text-slate-800 placeholder-slate-400 
                                focus:outline-none focus:border-[#422568]/50 focus:ring-1 focus:ring-[#422568]/20 transition-all duration-200`}
                            placeholder="e.g. Core Switch SNMP"
                        />
                        {errors.credential_name && (
                            <p className="mt-1 text-[9px] text-red-400 font-bold uppercase tracking-tight flex items-center">
                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                {errors.credential_name}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="flex items-center text-[10px] font-bold text-gray-800 uppercase tracking-widest mb-1.5">
                            <Shield className="w-3 h-3 mr-1.5 text-blue-400" />
                            Credential Type <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <CustomDropdown
                            data={credentialTypeOptions}
                            placeholder="Select Type"
                            selectedValue={formData.credential_type}
                            onSelect={(value) => {
                                setFormData(prev => ({
                                    ...prev,
                                    credential_type: value as 'snmp' | 'firewall' | 'vmware' | 'camera' | 'discovery' | 'ilo' | 'nvr',
                                    snmp_type: value === 'snmp' ? 'v1' : (value === 'nvr' ? 'v2c' : undefined),
                                    firewall_type: value === 'firewall' ? 'cisco' : undefined,
                                    vmware_type: value === 'vmware' ? 'normal' : undefined,
                                    cidrs: value === 'discovery' ? prev.cidrs : [],
                                    host: value === 'ilo' ? prev.host : '',
                                    port: value === 'ilo' || value === 'vmware' ? prev.port : undefined,
                                    log_monitor_enable: false,
                                    syslog_port: 514
                                }));
                                if (value === 'discovery') {
                                    setSelectedAsset(null);
                                    setIpAddresses([]);
                                }
                                setErrors({});
                            }}
                            width="w-full"
                            buttonHeight={8}
                        />
                    </div>
                </div>

                {/* IP Addresses Section - Redesigned for Compactness */}
                <div className="">

                    {formData.credential_type !== 'discovery' && (
                        <div className="mb-4">
                            <SingleSelectApiDropdown
                                label="Search Asset to add IP"
                                apiEndpoint="/assets"
                                displayProperty="name"
                                secondaryDisplayProperty="ip_address"
                                searchFields={['name', 'ip_address', 'hostname']}
                                searchInputPlaceholder="Search assets..."
                                placeholder="Select an asset"
                                icon={Server}
                                className="w-full"
                                value={selectedAsset}
                                error={errors.asset_id}
                                additionalQueries={{
                                    client_id: Number(agent.client_id),
                                    asset_type: { $notin: ['isp'] }
                                }}
                                onChange={(asset) => {
                                    setSelectedAsset(asset);
                                    setErrors(prev => {
                                        const { asset_id, ...rest } = prev;
                                        return rest;
                                    });
                                    if (asset && asset.ip_address) {
                                        setIpAddresses([asset.ip_address]);
                                        showSuccess(`Target IP set to: ${asset.ip_address}`);
                                    } else {
                                        setIpAddresses([]);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={newIpAddress}
                            onChange={(e) => setNewIpAddress(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddIpAddress();
                                }
                            }}
                            className={`flex-1 bg-white border ${ipErrors ? 'border-red-500/50' : 'border-gray-700/50'} rounded px-2 py-1.5 
                                text-white placeholder-gray-600 font-mono text-[10px]
                                focus:outline-none focus:border-[#422568]/50 transition-all`}
                            placeholder="Enter IP (e.g. 192.168.1.1)"
                        />
                        <button
                            type="button"
                            onClick={handleAddIpAddress}
                            disabled={!newIpAddress.trim()}
                            className="flex items-center gap-1.5 px-3 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-600/30 rounded text-[10px] font-bold uppercase transition-all disabled:opacity-30"
                        >
                            <Plus className="w-3 h-3" />
                            Add
                        </button>
                    </div> */}
                    {/* {ipErrors && <p className="mb-2 text-[9px] text-red-400 font-bold uppercase">{ipErrors}</p>} */}
                    {errors.ip_addresses && <p className="mb-2 text-[9px] text-red-400 font-bold uppercase">{errors.ip_addresses}</p>}

                    {/* {ipAddresses.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto custom-scroll">
                            {ipAddresses.map((ip, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-1.5 bg-white border border-gray-700 rounded px-2 py-0.5"
                                >
                                    <span className="text-gray-800 font-mono text-[10px]">{ip}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveIpAddress(index)}
                                        className="text-gray-800 hover:text-red-400 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )} */}
                </div>

                {/* Dynamic Credential Fields - Compact Grid */}
                <div className="bg-slate-50 border-l-2 border-[#422568]/30 rounded-r p-3 space-y-3">
                    {renderCredentialFields()}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-800">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-gray-800 text-[10px] font-bold uppercase tracking-widest transition-all"
                >
                    <X className="w-3.5 h-3.5" />
                    Discard
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-1.5 bg-[#422568] hover:bg-[#422568] text-white rounded text-[11px] font-bold shadow-lg shadow-[#422568]/20 transition-all uppercase tracking-widest disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <>
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                            Syncing...
                        </>
                    ) : (
                        <>
                            <Check className="w-3.5 h-3.5" />
                            {isEdit ? 'Update' : 'Save Config'}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CredentialForm;
